/**
 * TestConnection Component
 * Step 3: Test datasource connection with real-time progress
 */

import React, { useState, useEffect } from 'react';
import { datasourceService } from '../../services/datasources.service';

type DatasourceType = 'salesforce' | 'sap_erp' | 'odoo';

interface TestConnectionProps {
  type: DatasourceType;
  credentials: Record<string, any>;
  onSuccess: () => void;
  onBack: () => void;
}

type TestStep = 'idle' | 'connecting' | 'authenticating' | 'verifying' | 'success' | 'error';

export const TestConnection: React.FC<TestConnectionProps> = ({
  type,
  credentials,
  onSuccess,
  onBack,
}) => {
  const [currentStep, setCurrentStep] = useState<TestStep>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [testing, setTesting] = useState(false);

  const steps = [
    { key: 'connecting', label: 'Connecting to instance', icon: '🔌' },
    { key: 'authenticating', label: 'Authenticating credentials', icon: '🔐' },
    { key: 'verifying', label: 'Verifying access', icon: '✓' },
  ];

  const runTest = async () => {
    setTesting(true);
    setCurrentStep('connecting');
    setErrorMessage('');

    try {
      // Step 1: Connecting
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setCurrentStep('authenticating');

      // Step 2: Authenticating
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setCurrentStep('verifying');

      // Step 3: Verifying - actual API call
      const result = await datasourceService.testConnectionWithCredentials(type, credentials);

      if (result.success) {
        setCurrentStep('success');
      } else {
        setCurrentStep('error');
        setErrorMessage(result.message);
      }
    } catch (error: any) {
      setCurrentStep('error');
      setErrorMessage(error.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    runTest();
  }, []);

  const getStepStatus = (stepKey: string) => {
    const stepIndex = steps.findIndex((s) => s.key === stepKey);
    const currentIndex = steps.findIndex((s) => s.key === currentStep);

    if (currentStep === 'error') {
      if (stepIndex <= currentIndex) return 'error';
      return 'pending';
    }

    if (currentStep === 'success' || stepIndex < currentIndex) return 'success';
    if (stepKey === currentStep) return 'loading';
    return 'pending';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-extrabold text-gray-900 mb-1 tracking-tight">
          Testing Connection
        </h3>
        <p className="text-gray-600 mb-6">
          Verifying your credentials and connection to the platform
        </p>
      </div>

      {/* Test Steps */}
      <div className="space-y-4">
        {steps.map((step) => {
          const status = getStepStatus(step.key);

          return (
            <div
              key={step.key}
              className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${status === 'success' ? 'border-green-200 bg-green-50' : ''} ${status === 'loading' ? 'border-cyan-200 bg-cyan-50' : ''} ${status === 'error' ? 'border-red-200 bg-red-50' : ''} ${status === 'pending' ? 'border-gray-200 bg-gray-50' : ''}`}
            >
              {/* Icon */}
              <div className="flex-shrink-0">
                {status === 'success' && (
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {status === 'loading' && (
                  <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                )}
                {status === 'error' && (
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                {status === 'pending' && (
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                    {step.icon}
                  </div>
                )}
              </div>

              {/* Label */}
              <div className="flex-1">
                <p
                  className={`font-medium ${status === 'success' ? 'text-green-700' : ''} ${status === 'loading' ? 'text-cyan-700' : ''} ${status === 'error' ? 'text-red-700' : ''} ${status === 'pending' ? 'text-gray-500' : ''}`}
                >
                  {step.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Success Message */}
      {currentStep === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-green-800">
              <p className="font-medium">Connection Successful!</p>
              <p className="text-xs mt-1">
                Your credentials have been verified and the connection is working properly.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {currentStep === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-red-800">
              <p className="font-medium">Connection Failed</p>
              <p className="text-xs mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={testing}
          className="btn btn-g"
        >
          Back
        </button>

        {currentStep === 'error' && (
          <button
            type="button"
            onClick={runTest}
            disabled={testing}
            className="btn btn-g"
          >
            Retry
          </button>
        )}

        <button
          type="button"
          onClick={onSuccess}
          disabled={currentStep !== 'success'}
          className="btn btn-b flex-1"
        >
          {currentStep === 'success' ? 'Continue to Settings' : 'Testing...'}
        </button>
      </div>
    </div>
  );
};
